import {
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
  openWarningNotification,
  sanitizeSignerEmail,
} from "helpers/methods";
import {
  addContact,
  getContacts,
  getContactsCount,
  updateContact,
  uploadBulkContactCsv,
  uploadContactCsv,
} from "./ContactsService";
import {
  AddContactFields,
  ContactCsvRow,
  pageFilters,
  RequestParticipant,
} from "models/interfaces";
import strings from "global/constants/StringConstants";
import {
  addAllErrorCsvSigner,
  setLoading,
  setReduxSignRequest,
  setUploadedCsvFileName,
} from "redux/appSlice";

export const getTableData = async (
  pageNum = 1,
  setIsLoading: Function,
  setCount: Function,
  setHasMore: Function,
  setContacts: Function,
  contactsFilters: pageFilters,
) => {
  try {
    setIsLoading(true);
    const body = {
      searchText: contactsFilters.searchText,
      entitySorters: [],
    };
    const [data, count] = await Promise.all([
      getContacts(body, 10, pageNum),
      getContactsCount(body),
    ]);

    setCount(count);
    setHasMore(data.length >= 10);

    setContacts((prev: any) => (pageNum === 1 ? data : [...prev, ...data]));
  } catch (error: any) {
    handleErrorMessage(error);
  } finally {
    setIsLoading(false);
  }
};

export const AddUpdateContactHandler = async (
  setIsSaving: Function,
  setCount: Function,
  setHasMore: Function,
  setContacts: Function,
  contactsFilters: pageFilters,
  form: AddContactFields,
  errors: any,
  setErrors: Function,
  isCreatingNew: boolean,
  setIsCreatingNew: Function,
  setSelectedIndex: Function,
  onSuccess?: () => void,
  existingContacts?: AddContactFields[],
) => {
  try {
    if (handleValidation(form, errors, setErrors)) {
      setIsSaving(true);
      if (isCreatingNew) {
        const emailToAdd = form.emailId?.trim().toLowerCase();
        let isDuplicate =
          existingContacts &&
          existingContacts.some(
            (c: AddContactFields) =>
              c.emailId?.trim().toLowerCase() === emailToAdd,
          );

        if (!isDuplicate && emailToAdd) {
          try {
            const searchRes = await getContacts(
              { searchText: emailToAdd, entitySorters: [] },
              10,
              1,
            );
            isDuplicate = searchRes.some(
              (c: AddContactFields) =>
                c.emailId?.trim().toLowerCase() === emailToAdd,
            );
          } catch (error) {
            console.error("Error checking duplicate contact email", error);
          }
        }

        if (isDuplicate) {
          setIsSaving(false);
          openErrorNotification(
            `The email address ${form.emailId} is already present in your contacts.`,
          );
          return;
        }
      }
      const address = form.address
        ? Object.fromEntries(
            Object.entries(form.address).filter(([, v]) => isTruthy(v)),
          )
        : {};
      const body: any = {
        emailId: sanitizeSignerEmail(form.emailId) || form.emailId,
        firstName: form.firstName,
        lastName: form.lastName,
        status: "Active",
      };
      if (isTruthy(form.contactNo)) body.contactNo = form.contactNo;
      if (isTruthy(form.jobTitle)) body.jobTitle = form.jobTitle;
      if (isTruthy(form.companyName)) body.companyName = form.companyName;
      if (isTruthy(form.department)) body.department = form.department;
      if (isTruthy(form.managerEmail))
        body.managerEmail =
          sanitizeSignerEmail(form.managerEmail) || form.managerEmail;
      if (Object.keys(address).length > 0) body.address = address;

      if (isCreatingNew) {
        await addContact(body);
        openSuccessNotification("Contact created successfully!");
        setIsCreatingNew(false);
      } else {
        await updateContact(body);
        openSuccessNotification("Contact updated successfully!");
      }
      await getTableData(
        1,
        () => {},
        setCount,
        setHasMore,
        setContacts,
        contactsFilters,
      );
      const updatedContacts = await getContacts(
        {
          searchText: contactsFilters.searchText,
          entitySorters: [],
        },
        10,
        1,
      );

      const cleanedFormEmail =
        sanitizeSignerEmail(form.emailId) || form.emailId;
      const contactIndex = updatedContacts.findIndex(
        (contact: any) => contact.emailId === cleanedFormEmail,
      );

      if (contactIndex !== -1) {
        setSelectedIndex(contactIndex);
      }
      setIsSaving(false);
      if (onSuccess) onSuccess();
    }
  } catch (error: any) {
    setIsSaving(false);
    handleErrorMessage(error);
  }
};

export const handleValidation = (
  form: AddContactFields,
  errors: any,
  setErrors: Function,
) => {
  let isValid = true;
  const emailRegex = strings.regex;
  let error = { ...errors };
  const firstName = form.firstName;
  const lastName = form.lastName;
  const email = sanitizeSignerEmail(form.emailId)?.trim();

  if (!isTruthy(firstName)) {
    error.firstName = "Please enter first name!";
    isValid = false;
  } else {
    delete error.firstName;
  }
  if (!isTruthy(lastName)) {
    error.lastName = "Please enter last name!";
    isValid = false;
  } else {
    delete error.lastName;
  }
  if (!isTruthy(email)) {
    error.emailId = "Please enter email!";
    isValid = false;
  } else if (!emailRegex.test(email)) {
    error.emailId = "Please enter valid email id!";
    isValid = false;
  } else {
    delete error.emailId;
  }
  const managerEmail = sanitizeSignerEmail(form.managerEmail)?.trim();
  // if (isTruthy(managerEmail) && !emailRegex.test(managerEmail!)) {
  if (isTruthy(managerEmail)) {
    if (!emailRegex.test(managerEmail!)) {
      error.managerEmail = "Please enter a valid manager email!";
      isValid = false;
    } else if (managerEmail!.length > 255) {
      error.managerEmail = "Manager email cannot exceed 255 characters!";
      isValid = false;
    } else {
      delete error.managerEmail;
    }
  } else {
    delete error.managerEmail;
  }

  const contactNo = form.contactNo?.trim();
  if (isTruthy(contactNo) && contactNo!.length > 50) {
    error.contactNo = "Contact number cannot exceed 50 characters!";
    isValid = false;
  } else {
    delete error.contactNo;
  }

  const jobTitle = form.jobTitle?.trim();
  if (isTruthy(jobTitle) && jobTitle!.length > 150) {
    error.jobTitle = "Job title cannot exceed 150 characters!";
    isValid = false;
  } else {
    delete error.jobTitle;
  }

  const companyName = form.companyName?.trim();
  if (isTruthy(companyName) && companyName!.length > 150) {
    error.companyName = "Company name cannot exceed 150 characters!";
    isValid = false;
  } else {
    delete error.companyName;
  }

  const department = form.department?.trim();
  if (isTruthy(department) && department!.length > 150) {
    error.department = "Department cannot exceed 150 characters!";
    isValid = false;
  } else {
    delete error.department;
  }
  setErrors(error);
  return isValid;
};

export const fetchMoreContacts = async (
  page: number,
  setPage: Function,
  contactsFilters: pageFilters,
  setHasMore: Function,
  setContacts: Function,
) => {
  const nextPage = page + 1;
  const { searchText } = contactsFilters;
  const body = {
    searchText,
    entitySorters: [],
  };
  try {
    const data = await getContacts(body, 10, nextPage);
    if (data.length > 0) {
      setContacts((prev: any) => [...prev, ...data]);
      setPage(nextPage);
    } else {
      setHasMore(false);
    }
  } catch (error) {
    handleErrorMessage(error as any);
  }
};

export const onAddCsv = async (
  file: any,
  setIsLoading: Function,
  setCount: Function,
  setHasMore: Function,
  setContacts: Function,
  contactsFilters: pageFilters,
  setIsUploadCsvOpen: Function,
  setInvalidCsvRows: Function,
) => {
  try {
    const uploadFile = Array.isArray(file) ? file[0] : file;
    if (!uploadFile) return;
    let formData = new FormData();
    formData.append("file", uploadFile, uploadFile.name);
    setIsLoading(true);
    const res = await uploadContactCsv(formData);
    setIsLoading(false);

    // The endpoint returns a bare array of the rows it rejected (invalid
    // email); accepted rows are added/updated silently and aren't included.
    const invalidRows: ContactCsvRow[] = Array.isArray(res) ? res : [];

    if (invalidRows.length > 0) {
      setInvalidCsvRows(invalidRows);
      openWarningNotification(
        `${invalidRows.length} row${invalidRows.length === 1 ? "" : "s"} could not be imported — see details below.`,
      );
    } else {
      setInvalidCsvRows([]);
      setIsUploadCsvOpen(false);
      openSuccessNotification("File uploaded successfully");
    }

    getTableData(
      1,
      setIsLoading,
      setCount,
      setHasMore,
      setContacts,
      contactsFilters,
    );
  } catch (error: any) {
    setIsLoading(false);
    handleErrorMessage(error);
  }
};

export const exportCSVFile = (
  headers: string[],
  exampleRows: string[][],
  title: string,
) => {
  const escapeCell = (cell: string) =>
    /[",\r\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;

  const convertToCSV = () =>
    [headers, ...exampleRows]
      .map((row) => row.map(escapeCell).join(","))
      .join("\r\n") + "\r\n";

  const csv = convertToCSV();
  const exportedFilename = title + ".csv" || "export.csv";
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", exportedFilename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const uploadContact = async (file: any, dispatch: Function) => {
  try {
    let formData = new FormData();
    formData.append("file", file, file.name);
    dispatch(setLoading(true));
    const res = await uploadBulkContactCsv(formData);
    if (res) {
      dispatch(setUploadedCsvFileName([file.name]));
      const contactsWithIds = res[200].map(
        (contact: Partial<RequestParticipant>, index: number) => {
          contact.id = index;
          contact.color = "primary.main";
          contact.order = 1;
          contact.isValid = true;
          return contact;
        },
      );

      const inValidContacts = res[500].map(
        (contact: Partial<RequestParticipant>, index: number) => {
          contact.id = index;
          contact.color = "error.main";
          contact.order = 1;
          contact.isValid = false;
          return contact;
        },
      );

      dispatch(
        setReduxSignRequest({
          signers: [...contactsWithIds, ...inValidContacts],
        }),
      );
      dispatch(addAllErrorCsvSigner(res[500]));
      return { 200: res[200].length, 500: res[500].length };
      // dispatch(addFileInSignRequest({ unsignedPdf: file.name }));
    }
  } catch (error: any) {
    handleErrorMessage(error);
  } finally {
    dispatch(setLoading(false));
  }
};
